import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useState } from 'react';
import { PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import CoreSeatsExplainerModal from 'ui-addon-upgrades/_core/common/components/seatsModals/CoreSeatsExplainerModal';
import { planBuilderQuantityUpdated } from '../../actions/PlanBuilderActions';
import { CREATE_A_BUNDLE } from '../../constants/tracking/TrackingSources';
import { useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { planBuilderCommonTermForProductConfigurationsSelector, planBuilderProductConfigurationsSelector } from '../../selectors/PlanBuilderSelectors';
import { calculatedPriceForProductConfigurationsInListSelector, calculatedPriceSelector, planAdditionalCostsForProductConfigurationInListSelector } from '../../selectors/PricingSelectors';
import { includedCoreSeatsQuantitySelector, productLevelForCoreSeatsSelector } from '../../selectors/ProductConfigsSelectors';
import { coreSeatSkuForProductLevelSelector, productForSkuIdSelector } from '../../selectors/ProductsSelectors';
import { trackAdditionalUnitsPicker, trackModalInteraction } from '../../utils/tracking/trackingEventUtils';
import { StyledTitleContainer } from './SharedComponents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CoreSeatsPickerForPlanBuilder = () => {
  const dispatch = useAppDispatch();
  const productConfigurations = useAppSelector(planBuilderProductConfigurationsSelector);
  const commonTermForProductConfigurations = useAppSelector(planBuilderCommonTermForProductConfigurationsSelector);
  const [coreSeatsExplainerVisible, setCoreSeatsExplainerVisible] = useState(false);
  const productLevelForCoreSeats = useAppSelector(state => productLevelForCoreSeatsSelector(state, {
    productConfigurations
  }));
  const {
    skuId: skuIdCoreSeat
  } = useAppSelector(state => coreSeatSkuForProductLevelSelector(state, {
    productLevel: productLevelForCoreSeats
  }));
  const coreSeatConfig = productConfigurations.find(({
    skuId
  }) => skuId === skuIdCoreSeat);
  const includedCoreSeatsQuantity = useAppSelector(state => {
    return includedCoreSeatsQuantitySelector(state, {
      productConfigurations
    });
  });
  const additionalCoreSeats = (coreSeatConfig === null || coreSeatConfig === void 0 ? void 0 : coreSeatConfig.quantity) || 0;
  const {
    apiName
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: skuIdCoreSeat
  }));
  const handleCoreSeatsChange = ({
    target: {
      value
    }
  }) => {
    const newQuantityWithoutIncludedCoreSeats = parseInt(value, 10) - includedCoreSeatsQuantity;
    const oldQuantityWithCoreSeats = additionalCoreSeats + includedCoreSeatsQuantity;
    dispatch(planBuilderQuantityUpdated({
      quantity: newQuantityWithoutIncludedCoreSeats,
      skuId: skuIdCoreSeat
    }));
    trackAdditionalUnitsPicker({
      action: `stepper ${newQuantityWithoutIncludedCoreSeats + includedCoreSeatsQuantity > oldQuantityWithCoreSeats ? 'increase' : 'decrease'}`,
      trackingSource: CREATE_A_BUNDLE,
      productName: apiName,
      skuId: skuIdCoreSeat,
      ownedUnitQuantity: 0,
      displayedUnitQuantity: newQuantityWithoutIncludedCoreSeats + includedCoreSeatsQuantity
    });
  };
  const currencyCode = useDisplayedCurrencyCode();
  const additionalSeatsPrice = useAppSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: skuIdCoreSeat
    })],
    queryParamTerm: commonTermForProductConfigurations,
    displayedCurrencyCode: currencyCode
  }).monthly.calculatedPrice);
  const price = useAppSelector(state => planAdditionalCostsForProductConfigurationInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: productConfigurations.filter(config => config.skuId === skuIdCoreSeat),
    queryParamTerm: commonTermForProductConfigurations,
    displayedCurrencyCode: currencyCode
  }));
  const coreSeatProPrice = useAppSelector(state => {
    const {
      skuId: coreSeatSkuId
    } = coreSeatSkuForProductLevelSelector(state, {
      productLevel: PROFESSIONAL
    });
    return calculatedPriceSelector(state, {
      productConfigurations: [createProductConfig({
        skuId: coreSeatSkuId
      })],
      displayedCurrencyCode: currencyCode
    });
  });
  const handleLinkClick = () => {
    setCoreSeatsExplainerVisible(true);
    trackModalInteraction({
      action: 'click to open',
      name: 'core-seats-explainer-modal',
      productLevel: productLevelForCoreSeats,
      trackingSource: CREATE_A_BUNDLE
    });
  };
  const TotalCoreSeatsText = () => /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `interstitial.coreSeats.add.${productLevelForCoreSeats}_jsx`,
    elements: {
      Link: ({
        children
      }) => /*#__PURE__*/_jsx(Link, {
        onClick: handleLinkClick,
        children: children
      })
    }
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [coreSeatsExplainerVisible && /*#__PURE__*/_jsx(CoreSeatsExplainerModal, {
      handleModalClose: () => setCoreSeatsExplainerVisible(false),
      currencyCode: currencyCode,
      coreSeatProPrice: coreSeatProPrice.monthly.calculatedPrice
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(StyledTitleContainer, {
          children: /*#__PURE__*/_jsx(Text, {
            typography: "heading-50",
            children: /*#__PURE__*/_jsx(TotalCoreSeatsText, {})
          })
        }), /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          marginBlockEnd: "400",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "planBuilder.priceDisplay.perMonthPerSeat",
            options: {
              price: /*#__PURE__*/_jsx(PriceDisplay, {
                price: additionalSeatsPrice,
                currencyCode: currencyCode
              })
            }
          })
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "end",
        flexDirection: "column",
        maxInlineSize: "100px",
        children: [/*#__PURE__*/_jsx(StepperInput, {
          value: additionalCoreSeats + includedCoreSeatsQuantity,
          min: includedCoreSeatsQuantity,
          onChange: handleCoreSeatsChange,
          "data-selenium-id": "core-seats-card-stepper-input",
          disabledMinButtonTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "interstitial.seatsPicker.disabledMinButtonTooltip"
          }),
          disabledMinButtonTooltipProps: {
            placement: 'bottom',
            children: /*#__PURE__*/_jsx(_Fragment, {})
          }
        }), /*#__PURE__*/_jsxs(Text, {
          as: "span",
          marginInlineStart: "200",
          children: [/*#__PURE__*/_jsx(PriceDisplay, {
            price: price.monthly.calculatedPrice,
            currencyCode: currencyCode
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.priceDisplay.perMonth"
          })]
        })]
      })]
    })]
  });
};
export default CoreSeatsPickerForPlanBuilder;